// @flow
import { useContext } from 'react';
import { AppContext } from 'contexts/AppContext';
import { getProfileInfo } from 'services/userService';
import { getLocalAuth } from 'utils/auth';
import useFortressQuery from './useFortressQuery';

type UserProfile = {
  portalUserId: string,
  firstName: string,
  lastName: string,
  preferredName: string,
  customerStatus: string,
  phoneNumber: string,
  emailAddress: string,
  applicantId: string,
  customerId: string,
  isPrimaryProspect: boolean,
  isFinanciallyResponsible: boolean,
  isAffordable: boolean,
  areFeesApplied: boolean,
  areFeesPaid: boolean,
  prospectPreferences: {
    id: string,
    nBeds: null,
    nBedsArr: [],
    preferredFloorPlanId: string,
    nBaths: null,
    nHalfBaths: null,
    priceFrom: null,
    priceTo: null,
    leaseTermInMonths: null,
    moveInDateFrom: string,
    moveInDateTo: string,
    moveInDateScheduled: null,
    wants: null,
    notWants: null,
    helpText: null,
    hasUserChosenToProvideMoveInPreferences: null,
    createdAt: string,
    updatedAt: string,
    prospectId: string,
    unitId: string,
  },
};

const placeholderData = {};
export const getUserProfileQueryKey = ({
  organizationId,
  propertyId,
  portalUserId,
}) => ['getProfileInfo', organizationId, propertyId, portalUserId ?? null];

const useUserProfileQuery = (queryOptions = {}) => {
  const { organizationId, propertyId } = useContext(AppContext);
  const portalUserId = getLocalAuth()?.authUser?.id ?? null;
  const queryKey = getUserProfileQueryKey({
    organizationId,
    propertyId,
    portalUserId,
  });

  return useFortressQuery({
    queryKey,
    queryFn: async ({ signal }): Promise<UserProfile> => {
      return getProfileInfo(
        {
          organizationId,
          propertyId,
          portalUserId,
        },
        { signal },
      );
    },
    enabled: !!organizationId && !!propertyId && !!portalUserId,
    placeholderData,
    // staleTime: 0 means profile data is always considered stale.
    // Combined with refetchOnMount and refetchOnWindowFocus, this guarantees
    // a background network fetch on every app mount and every time the user
    // returns to the tab — critical because customer status, fee state, and
    // applicant IDs can change out-of-band (payments, Fortress admin actions,
    // server-side resets). The persisted 24h cache still provides an instant
    // display while the fresh fetch is in flight, so there is no loading flash.
    staleTime: 0,
    // Refetch when the query mounts. Since staleTime is 0 this always triggers
    // a network request — it effectively mirrors 'always' without the overhead
    // of refetching every observer subscription inside the same render cycle.
    refetchOnMount: true,
    // Opt this query into window-focus refetching even though the global default
    // is false. User profile is lightweight and must be current when the user
    // returns to the app after paying fees, signing a lease, or taking any
    // off-portal action that changes their status.
    refetchOnWindowFocus: true,
    // Keep profile in sync after temporary network interruptions.
    refetchOnReconnect: true,
    ...queryOptions,
  });
};

export default useUserProfileQuery;
